const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const FREE_CAPACITY_LIMIT = 15;
const PLUS_CAPACITY_LIMIT = 30;
const PREMIUM_CAPACITY_LIMIT = 50;
const STAFF_CAPACITY_LIMIT = 100;

const normalizePricingModel = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "host_paid" || normalized === "shared_cost") {
    return normalized;
  }
  return "free";
};

const normalizeSubscriptionTier = (hostRating) => {
  const explicitTier = String(hostRating?.subscription_tier || "").trim().toLowerCase();
  if (explicitTier === "premium" || explicitTier === "plus" || explicitTier === "free") {
    return explicitTier;
  }
  if (hostRating?.has_premium_subscription) return "premium";
  if (hostRating?.has_plus_access) return "plus";
  return "free";
};

export const resolveEventCapacityState = ({
  hostRating,
  formValue,
  existingParticipantsCount = 0,
  existingMaxParticipants = 0,
  mode = "create",
  pricingModel = "free",
  hasPaidHostAccess = false,
}) => {
  const minParticipantsLimit = Math.max(
    3,
    Number(
      hostRating?.selectable_min_participants_limit ??
        hostRating?.min_participants_limit ??
        3
    ) || 3
  );
  const baseMaxParticipantsLimit = Math.max(
    minParticipantsLimit,
    FREE_CAPACITY_LIMIT
  );
  const hardMaxParticipantsLimit = Math.max(
    baseMaxParticipantsLimit,
    PREMIUM_CAPACITY_LIMIT,
    Number(hostRating?.hard_max_participants_limit ?? PREMIUM_CAPACITY_LIMIT) ||
      PREMIUM_CAPACITY_LIMIT
  );
  const participantsPerSuccess = 0;
  const freeMaxParticipantsLimit = Math.min(
    hardMaxParticipantsLimit,
    Math.max(baseMaxParticipantsLimit, FREE_CAPACITY_LIMIT)
  );
  const plusMaxParticipantsLimit = Math.min(
    hardMaxParticipantsLimit,
    Math.max(freeMaxParticipantsLimit, PLUS_CAPACITY_LIMIT)
  );
  const premiumMaxParticipantsLimit = Math.min(
    hardMaxParticipantsLimit,
    Math.max(plusMaxParticipantsLimit, PREMIUM_CAPACITY_LIMIT)
  );
  const isStaffCapacityHost = Boolean(hostRating?.is_staff || hostRating?.is_superuser);
  const staffMaxParticipantsLimit = Math.max(
    premiumMaxParticipantsLimit,
    isStaffCapacityHost ? STAFF_CAPACITY_LIMIT : 0,
    Number(hostRating?.staff_max_participants_limit ?? 0) || premiumMaxParticipantsLimit
  );
  const hostPaidMaxParticipantsLimit = Math.max(
    premiumMaxParticipantsLimit,
    isStaffCapacityHost ? staffMaxParticipantsLimit : 0,
    Number(
      hostRating?.host_paid_max_participants_limit ??
        hostRating?.hard_max_participants_limit ??
        50
    ) || premiumMaxParticipantsLimit
  );
  const progressMaxParticipantsLimit = freeMaxParticipantsLimit;
  const subscriptionTier = normalizeSubscriptionTier(hostRating);

  let fallbackAccessibleMax = freeMaxParticipantsLimit;
  if (isStaffCapacityHost) {
    fallbackAccessibleMax = staffMaxParticipantsLimit;
  } else if (subscriptionTier === "plus") {
    fallbackAccessibleMax = plusMaxParticipantsLimit;
  } else if (subscriptionTier === "premium") {
    fallbackAccessibleMax = premiumMaxParticipantsLimit;
  }

  const existingCapacityFloor =
    mode === "edit" ? Math.max(0, Number(existingMaxParticipants) || 0) : 0;
  const participantsFloor = Math.max(
    minParticipantsLimit,
    Number(existingParticipantsCount) || 0
  );
  const normalizedPricingModel = normalizePricingModel(pricingModel);
  const usesHostPaidCapacity =
    normalizedPricingModel === "host_paid" && Boolean(hasPaidHostAccess);
  const reportedMaxParticipantsLimit =
    Number(hostRating?.max_participants_limit ?? 0) || fallbackAccessibleMax;
  const fallbackCapacityLimit = usesHostPaidCapacity
    ? hostPaidMaxParticipantsLimit
    : fallbackAccessibleMax;
  const reportedAccessibleLimit = usesHostPaidCapacity
    ? fallbackCapacityLimit
    : Math.max(fallbackCapacityLimit, reportedMaxParticipantsLimit);
  const accessibleMaxParticipantsLimit = Math.max(
    baseMaxParticipantsLimit,
    reportedAccessibleLimit,
    existingCapacityFloor,
    participantsFloor
  );
  const visualMaxParticipantsLimit = Math.max(
    usesHostPaidCapacity
      ? hostPaidMaxParticipantsLimit
      : premiumMaxParticipantsLimit,
    accessibleMaxParticipantsLimit,
    participantsFloor
  );
  const normalizedMaxParticipants = clamp(
    Number(formValue ?? participantsFloor) || participantsFloor,
    participantsFloor,
    visualMaxParticipantsLimit
  );

  return {
    successes: 0,
    minParticipantsLimit,
    baseMaxParticipantsLimit,
    hardMaxParticipantsLimit,
    participantsPerSuccess,
    freeMaxParticipantsLimit,
    plusMaxParticipantsLimit,
    premiumMaxParticipantsLimit,
    hostPaidMaxParticipantsLimit,
    staffMaxParticipantsLimit,
    progressMaxParticipantsLimit,
    subscriptionTier,
    participantsFloor,
    accessibleMaxParticipantsLimit,
    visualMaxParticipantsLimit,
    normalizedMaxParticipants,
    existingCapacityFloor,
    pricingModel: normalizedPricingModel,
    hasPaidHostAccess: Boolean(hasPaidHostAccess),
    capacityMode: isStaffCapacityHost ? "staff" : usesHostPaidCapacity ? "host_paid" : "subscription",
  };
};

export const resolveCapacityUpgradePrompt = (capacityState, selectedValue) => {
  if (capacityState.capacityMode === "host_paid") {
    return null;
  }
  const value = Number(selectedValue) || 0;
  if (value <= capacityState.accessibleMaxParticipantsLimit) {
    return null;
  }

  if (value <= capacityState.plusMaxParticipantsLimit) {
    return {
      kind: "plus",
      targetTier: "plus",
      targetLimit: capacityState.plusMaxParticipantsLimit,
    };
  }

  return {
    kind: "premium",
    targetTier: "premium",
    targetLimit: capacityState.premiumMaxParticipantsLimit,
  };
};
